import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Theme,
  Typography,
} from '@mui/material';
import { darken } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { theme } from '@/providers/ThemeProvider';
import { ChatRoomsTemplateMessage } from '@/types/graphql';

type TemplateMessageItemProps = {
  templates: ChatRoomsTemplateMessage[];
  language: string;
  templateMessageId?: string;
  isRemoteMode: boolean;
  onSend: (id: string) => void;
  onRemoteClickSend: (id: string) => void;
  onRef?: (element: HTMLButtonElement | null) => void;
  resendConfirmDialog: {
    open: (params: {
      title: string;
      message?: string;
      content?: ReactElement;
    }) => Promise<boolean>;
  };
};

const TemplateMessageItem = ({
  templates,
  language,
  templateMessageId,
  onSend,
  isRemoteMode,
  onRemoteClickSend,
  onRef,
  resendConfirmDialog,
}: TemplateMessageItemProps) => {
  const { t } = useTranslation('chat');

  if (templates.length === 0) throw new Error();
  const isGroup = templates.length >= 2;
  const firstItem = templates[0];

  const borderColor = useMemo(() => {
    if (!isRemoteMode) return undefined;

    if (firstItem.templateMessage.id === templateMessageId) {
      return `${darken(theme.palette.primary.main, 0.2)} !important`;
    }

    if (firstItem.hasChatMessage) {
      return (t: Theme) => `${t.palette.grey[300]} !important`;
    }

    return undefined;
  }, [
    firstItem.templateMessage.id,
    firstItem.hasChatMessage,
    templateMessageId,
    isRemoteMode,
  ]);

  const handleSend = async () => {
    if (isRemoteMode) {
      onRemoteClickSend(firstItem.templateMessage.id);
    } else {
      onSend(firstItem.templateMessage.id);
    }
  };

  return (
    <Button
      onClick={async () => {
        // 送信されている場合はダイアログを表示
        if (
          !isRemoteMode &&
          firstItem.hasChatMessage &&
          !(await resendConfirmDialog.open({
            title: '',
            message: t('templateSelect.confirmResend'),
          }))
        ) {
          return;
        }
        handleSend();
      }}
      sx={{
        width: 1,
        gridColumn: 2,
        backgroundColor: firstItem.hasChatMessage
          ? (t) => `${t.palette.grey[200]} !important`
          : undefined,
        borderColor,
        textTransform: 'none',
      }}
      variant="outlined"
      ref={onRef}
    >
      <Box
        width={1}
        display="grid"
        gridTemplateColumns="1fr auto"
        alignItems="center"
      >
        <Stack spacing={1}>
          {templates.map((x) => (
            <Box
              key={x.templateMessage.id}
              sx={
                isGroup
                  ? {
                      border: 1,
                      borderRadius: 1,
                      borderColor: (t) => t.palette.secondary.main,
                      py: 0.5,
                      px: 0.5,
                    }
                  : {}
              }
            >
              <Typography
                textAlign="left"
                sx={{
                  // 2行まで表示とし、それより多い場合は３点リーダー
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  '-webkit-line-clamp': '2',
                  '-webkit-box-orient': 'vertical',
                }}
              >
                {x.templateMessage.languages.find(
                  (y) => y.language === language
                )?.message ?? ''}
              </Typography>
            </Box>
          ))}
        </Stack>
        <IconButton
          size="small"
          sx={{
            width: 26,
            height: 26,
            mr: -2,
            ml: '-4px',
          }}
          onMouseDown={(e) => {
            // 親ボタンへのイベントを抑制
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (
              await resendConfirmDialog.open({
                title: firstItem.hasChatMessage
                  ? t('templateSelect.confirmResendBelow')
                  : t('templateSelect.confirmSendBelow'),
                content: (
                  <Stack spacing={1}>
                    {templates.map((x) => (
                      <Typography
                        key={x.templateMessage.id}
                        // textAlign="center"
                        sx={{
                          px: 2,
                          py: 1,
                          border: 1,
                          borderRadius: 1,
                          borderColor: (t) => t.palette.secondary.main,
                        }}
                      >
                        {
                          x.templateMessage.languages.find(
                            (y) => y.language === language
                          )?.message
                        }
                      </Typography>
                    ))}
                  </Stack>
                ),
              })
            ) {
              handleSend();
            }
          }}
        >
          <OpenInFullIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Button>
  );
};

export default TemplateMessageItem;
